import React, { useCallback, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { useRecoilValueLoadable } from 'recoil'
import styled from '@emotion/styled'
import { IconButton } from '@mui/material'
import { Close } from '@emotion-icons/material-outlined/Close'
import { MapSquareIcon } from 'src/components/icons/map-square'
import { Briefcase } from '@emotion-icons/fa-solid/Briefcase'
import { Colors, Shapes } from 'src/components/theme'
import { FlexColumn, FlexRow } from 'src/components/flex-containers'
import { Typography } from '@mui/material'
import { ListItem } from 'src/components/sidebar-panels/list-item'
import { TitledList } from 'src/components/sidebar-panels/titled-list'
import { CustomErrorBoundary } from 'src/components/errors/error-boundary'
import {
  useDeleteAllRecentlyViewed,
  useDeleteRecentlyViewed,
} from 'src/components/sidebar-panels/recently-viewed/hooks'
import { useBrandsRankLayer } from 'src/hooks/use-brands-rank-layer'
import { LocationArrow } from '@emotion-icons/fa-solid/LocationArrow'
import { LightTooltip } from 'src/components/tooltip/light-tooltip'
import { UserList, UserListItem } from 'src/common-services/types/user-lists-types'
import { MapSigns } from '@emotion-icons/fa-solid/MapSigns'
import { MapMarkerAlt } from '@emotion-icons/fa-solid/MapMarkerAlt'
import { VectorSquare } from '@emotion-icons/fa-solid/VectorSquare'
import { GenericLoader } from 'src/v2/components/skeleton-loaders'
import { useMapExplorerNavigation } from 'src/views/map-explorer/sidebar/search/use-navigation'
import { useMapExplorerChoice } from 'src/views/map-explorer/sidebar/search/use-choice'

import { recentViews } from 'src/atoms/recently-viewed'
import { supportedSelections } from 'src/v2/state/map-explorer/selector'

const IconContainer = styled.div`
  margin-right: ${Shapes.spacing[2]}px;
`

const RecentListEmptyWrapper = styled.div`
  padding: ${Shapes.spacing[4]}px;
  text-align: center;
`

const StyledListItem = styled(ListItem)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Shapes.spacing[2]}px ${Shapes.spacing[4]}px;

  button {
    opacity: 0;
  }
  &:hover button {
    opacity: 1;
  }
`

const ListItemContent = styled.div`
  display: flex;
  align-items: center;
`

const StyledIconButton = styled(IconButton)`
  align-self: center;
  color: ${Colors.textMedium};
`

type ListItemVariant = 'list-item' | 'suggestion-item'

const ITEM_THEME_STYLES = {
  'list-item': {
    iconColor: Colors.neutral400,
    textColor: Colors.neutral700,
    textVariant: undefined,
    subTextColor: Colors.neutral400,
    subTextVariant: 'text2m' as any,
    subTextFontStyle: undefined,
  },
  'suggestion-item': {
    iconColor: Colors.neutral300,
    textColor: Colors.neutral400,
    textVariant: 'textmd' as any,
    subTextColor: Colors.neutral300,
    subTextVariant: 'textmdm' as any,
    subTextFontStyle: 'italic',
  },
}

type RecentListItemProps = {
  item: UserListItem
  bordered?: boolean
  hideDelete?: boolean
  variant?: ListItemVariant
}

export const RecentListItem: React.FC<RecentListItemProps> = (props) => {
  const { item, bordered = true, hideDelete = false, variant = 'list-item' } = props
  const deleteRecentlyViewed = useDeleteRecentlyViewed()
  const { closeBrandsRankLayer } = useBrandsRankLayer()
  const { selectLocation, selectAddress, selectOwner, selectParcel } = useMapExplorerNavigation()
  const { chooseLocation, chooseAddress } = useMapExplorerChoice()
  const isEntitySelector = Boolean(useAtomValue(supportedSelections))

  const name = useMemo(() => {
    switch (item.type) {
      case 'place':
        return item.data.name
      case 'location':
        return item.data.name
      case 'custom-area':
        return item.data.name
      case 'parcel':
        return ''
      case 'owner':
        return item.data.name
      default:
        return ''
    }
  }, [item])

  const address = useMemo(() => {
    switch (item.type) {
      case 'place':
        return item.data.address
      case 'location':
        return item.data.address
      case 'custom-area':
        return item.data.description
      case 'parcel':
        return item.data.address
      case 'owner':
        return item.data.address
      default:
        return ''
    }
  }, [item])

  const icon = useMemo(() => {
    switch (item.type) {
      case 'place':
        return <MapSigns size={14} color={ITEM_THEME_STYLES[variant].iconColor} />
      case 'location':
        return <MapMarkerAlt size={14} color={ITEM_THEME_STYLES[variant].iconColor} />
      case 'custom-area':
        return <VectorSquare size={14} color={ITEM_THEME_STYLES[variant].iconColor} />
      case 'parcel':
        return <MapSquareIcon size={14} color={ITEM_THEME_STYLES[variant].iconColor} style={{ marginTop: 6 }} />
      case 'owner':
        return <Briefcase size={14} color={ITEM_THEME_STYLES[variant].iconColor} />
      default:
        return <LocationArrow size={14} color={ITEM_THEME_STYLES[variant].iconColor} />
    }
  }, [item, variant])

  const handleDelete = async (event: React.MouseEvent<HTMLButtonElement> | undefined) => {
    event?.stopPropagation()
    if (!item.id) {
      return
    }
    await deleteRecentlyViewed(item.id)
  }

  const onClick = useCallback(async () => {
    closeBrandsRankLayer() // in case layer is open, we need to close it in order to show the new selected place
    switch (item.type) {
      case 'place':
        isEntitySelector ? chooseAddress(item.data.googlePlaceId) : await selectAddress(item.data.googlePlaceId)
        break
      case 'location':
        isEntitySelector ? chooseLocation(item.data.locationId) : await selectLocation(item.data.locationId)
        break
      case 'parcel':
        if (!isEntitySelector && item.data.id) {
          await selectParcel(item.data.id)
        }
        break
      case 'owner':
        if (!isEntitySelector) {
          await selectOwner(item.data.ownerId)
        }
        break
    }
  }, [
    item,
    selectAddress,
    selectLocation,
    closeBrandsRankLayer,
    chooseAddress,
    chooseLocation,
    isEntitySelector,
    selectParcel,
    selectOwner,
  ])

  return (
    <StyledListItem onClick={onClick} bordered={bordered}>
      <ListItemContent>
        <IconContainer>{icon}</IconContainer>
        <div>
          {item.type !== 'place' && item.type !== 'parcel' ? (
            <>
              <Typography variant={ITEM_THEME_STYLES[variant].textVariant} color={ITEM_THEME_STYLES[variant].textColor}>
                {name}
              </Typography>
              <Typography
                variant={ITEM_THEME_STYLES[variant].subTextVariant}
                color={ITEM_THEME_STYLES[variant].subTextColor}
                fontStyle={ITEM_THEME_STYLES[variant].subTextFontStyle}>
                {address ? address.replace(/, USA$/, '') : ''}
              </Typography>
            </>
          ) : (
            <Typography
              variant={ITEM_THEME_STYLES[variant].textVariant}
              color={ITEM_THEME_STYLES[variant].textColor}
              style={{
                paddingTop: 6,
                paddingBottom: 6,
              }}>
              {address ? address.replace(/, USA$/, '') : ''}
            </Typography>
          )}
        </div>
      </ListItemContent>
      {!hideDelete && (
        <LightTooltip title="Delete" placement="bottom" arrow={false}>
          <StyledIconButton size="small" onClick={handleDelete}>
            <Close size={16} />
          </StyledIconButton>
        </LightTooltip>
      )}
    </StyledListItem>
  )
}

const RecentListEmpty: React.FC = () => {
  return (
    <RecentListEmptyWrapper>
      <Typography variant="title2m" color={Colors.textMedium}>
        Use the search bar above to start viewing brands and locations.
      </Typography>
    </RecentListEmptyWrapper>
  )
}

export const RecentlyViewedSkeletonLoader = () => {
  return (
    <FlexColumn gap={Shapes.spacing[2]}>
      {Array.from({ length: 4 }).map((_, index) => (
        <FlexRow
          key={index}
          gap={Shapes.spacing[2]}
          align="start"
          style={{
            padding: '8px 16px',
            borderBottom: index === 3 ? 'none' : `1px solid ${Colors.border}`,
          }}>
          <GenericLoader height={14} width={14} variant="rounded" />
          <FlexColumn>
            <GenericLoader height={16} width={120} variant="text" />
            <GenericLoader height={16} width={150} variant="text" color="light" />
          </FlexColumn>
        </FlexRow>
      ))}
    </FlexColumn>
  )
}

const Inner: React.FC = () => {
  const recentlyViewedLoadable = useRecoilValueLoadable(recentViews)
  const recentlyViewed = recentlyViewedLoadable.contents as UserList | undefined
  const deleteAllRecentlyViewed = useDeleteAllRecentlyViewed()
  const onClear = () => deleteAllRecentlyViewed()

  return (
    <TitledList
      titleTextStyle={{
        minHeight: '60px',
      }}
      listStyle={{
        paddingBottom: Shapes.spacing[8],
      }}
      title="Recently Viewed"
      clearable={recentlyViewedLoadable.state === 'hasValue' && recentlyViewed && recentlyViewed.items.length > 0}
      onClear={onClear}>
      {recentlyViewedLoadable.state === 'loading' ? (
        <RecentlyViewedSkeletonLoader />
      ) : !recentlyViewed || recentlyViewed.items.length === 0 ? (
        <RecentListEmpty />
      ) : (
        recentlyViewed.items.map((x) => {
          return <RecentListItem key={x.id} item={x} />
        })
      )}
    </TitledList>
  )
}

const ErrorDisplay = styled.div`
  padding: ${Shapes.spacing[1]}px ${Shapes.spacing[4]}px;
`

export const RecentlyViewed: React.FC = () => {
  return (
    <CustomErrorBoundary errorDisplay={<ErrorDisplay>Recently viewed not available.</ErrorDisplay>}>
      <React.Suspense fallback={<RecentlyViewedSkeletonLoader />}>
        <Inner />
      </React.Suspense>
    </CustomErrorBoundary>
  )
}
